import { z } from '@customerio/hydra-validation';
import type { ObjectType as UIAPIObjectType } from '@customerio/hydra-ui-api';
import { defineEntity, readSchema } from '../shared/define-entity';

/**
 * ObjectType entity — workspace-scoped object-type metadata (segments,
 * journeys, snippets, etc. can target specific object types).
 *
 * Backend at `/v1/environments/:environment_id/object_types`
 * (`listObjectTypes` in `services/ui_api/objects/list_types.go`). No
 * `created`/`updated` on the wire. Reads live in `queries.ts`;
 * create/update/delete live in `mutations.ts`.
 *
 * **`id` is `string`** per the layer convention even though the wire
 * encodes it as `number` — same Tag/SubscriptionTopic/Snippet coercion.
 */

export type ObjectType = Omit<UIAPIObjectType, 'id'> & {
  id: string;
};

const ObjectTypeFromApi = readSchema<ObjectType>(
  z.looseObject({
    id: z.number().transform(String),
  }),
);

/** @public */
export const objectType = defineEntity<ObjectType, ObjectType, void, undefined, 'workspace'>({
  name: 'object_type',
  scope: 'workspace',
  pk: (t) => t.id,
  schema: { read: ObjectTypeFromApi },
});

export const objectTypeKeys = objectType.keys;

export function parseObjectType(raw: UIAPIObjectType): ObjectType {
  return objectType.parse(raw);
}

/* ------------------------------------------------------------------------- */
/* Computed properties — selectors as plain functions                         */
/* ------------------------------------------------------------------------- */

/**
 * Direct port of the Ember `ObjectTypeModel#objectIcon` getter — falls
 * back to the generic `'object'` icon when the type has no custom one.
 *
 * Returned as a plain string rather than typed against `IconName` so
 * this util doesn't have to take a dependency on `@customerio/pluma-icons`;
 * call sites that pass this into a Pluma `<Icon name=…>` prop can cast.
 */
/** @public */
export function objectTypeIcon(type: Pick<ObjectType, 'icon'>): string {
  return type.icon != null && type.icon !== '' ? type.icon : 'object';
}

/** Resolve canonical slug URLs first, with the numeric-id legacy URL as fallback. */
export function getObjectTypeBySlug(types: readonly ObjectType[], slugOrId: string): ObjectType | undefined {
  return types.find(({ slug }) => slug === slugOrId) ?? types.find(({ id }) => id === slugOrId);
}

/** Include newly-written attributes immediately while the object API catches up. */
export function updateObjectTypeSentAttributes(
  type: ObjectType,
  attributes: Readonly<Record<string, string>>,
): ObjectType {
  return { ...type, sent_attributes: [...new Set([...(type.sent_attributes ?? []), ...Object.keys(attributes)])] };
}
